import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { providerGatewayAdaptersService } from '../../services/ProviderGatewayAdaptersService';
import { providerTenantsService } from '../../services/ProviderTenantsService';
import { opsDevicesService } from '../../services/OpsDevicesService';
import { providerEfemService } from '../../services/ProviderEfemService';
import { AppIconButton, AppPage } from '../../components/design-system';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import {
  SearchableTenantCombobox,
  tenantToSearchableOption,
  type SearchableTenantOption,
} from '../../components/common/SearchableTenantCombobox';
import * as dt from '@cnapse/shared/data.types.js';
import { PRODUCT_NAMES } from '@cnapse/shared';
import type { EfemAdapterAssignment, OpsDeviceDetail } from '@cnapse/shared/schemas.js';
import { EfemGatewayList, type EfemGatewayEntry } from '../../components/efem/setup/EfemGatewayList';
import { EfemGatewayListPanel } from '../../components/efem/setup/EfemGatewayListPanel';
import { EfemGatewayDetail } from '../../components/efem/setup/EfemGatewayDetail';
import { EfemGatewaySplitLayout } from '../../components/efem/setup/EfemGatewaySplitLayout';
import { GatewayAdapterAssignmentPanel } from '../../components/efem/GatewayAdapterAssignmentPanel';
import { EfemGatewayConfigPanel } from '../../components/efem/EfemGatewayConfigPanel';

function getQueryParam(key: string): string | null {
  return new URLSearchParams(window.location.search).get(key);
}

function setQueryParams(params: Record<string, string | null>) {
  const url = new URL(window.location.href);
  for (const [k, v] of Object.entries(params)) {
    if (v === null) url.searchParams.delete(k);
    else url.searchParams.set(k, v);
  }
  window.history.pushState({}, '', url.toString());
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function ProviderEfemSetupPage() {
  const [tenantOptions, setTenantOptions] = useState<SearchableTenantOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState<string | null>(null);

  const [selectedTenantId, setSelectedTenantId] = useState<string>(getQueryParam('tenantId') ?? '');
  const [gateways, setGateways] = useState<EfemGatewayEntry[]>([]);
  const [registeredSns, setRegisteredSns] = useState<Set<string>>(new Set());
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);

  const [selectedSn, setSelectedSn] = useState<string | null>(getQueryParam('gateway'));
  const [activeTab, setActiveTab] = useState<number>(Number(getQueryParam('tab') ?? '0'));
  const [selectedDevice, setSelectedDevice] = useState<OpsDeviceDetail | null>(null);
  const [deviceLoading, setDeviceLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const list = await providerTenantsService.getTenants();
        const options = list.map(tenantToSearchableOption);
        setTenantOptions(options);
        if (!selectedTenantId && options.length > 0) {
          setSelectedTenantId(options[0].id);
        }
      } catch (e) {
        setTenantsError(e instanceof Error ? e.message : 'Failed to load tenants');
      } finally {
        setTenantsLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClients = useCallback(async () => {
    if (!selectedTenantId) return;
    setClientsLoading(true);
    setClientsError(null);
    try {
      const [clientsRes, devicesRes] = await Promise.all([
        providerEfemService.getProviderEfemClients(selectedTenantId),
        providerEfemService.getProviderTenantDevices(selectedTenantId),
      ]);

      const tenantAssignedGateways = devicesRes.data.devices.filter(
        (d) => d.deviceCategory === 'efem-gateway',
      );
      const tenantGatewaySns = new Set(tenantAssignedGateways.map((d) => d.serialNumber));

      const registeredEntries: EfemGatewayEntry[] = clientsRes.clients
        .filter(
          (c) => !!c.gatewaySerialNumber && tenantGatewaySns.has(c.gatewaySerialNumber),
        )
        .map((c) => ({
          sn: c.gatewaySerialNumber as string,
          name: c.name,
          status: c.status as dt.ONLINE_OFFLINE_STATUS,
          inventoryStatus: 'active' as const,
          isRegistered: true,
        }));

      const registeredSnSet = new Set(registeredEntries.map((e) => e.sn));
      setRegisteredSns(registeredSnSet);

      const unregisteredEntries: EfemGatewayEntry[] = tenantAssignedGateways
        .filter((d) => !registeredSnSet.has(d.serialNumber))
        .map((d) => ({
          sn: d.serialNumber,
          name: d.serialNumber,
          status: 'offline' as dt.ONLINE_OFFLINE_STATUS,
          inventoryStatus: d.inventoryStatus ?? 'claimed',
          isRegistered: false,
        }));

      setGateways([...registeredEntries, ...unregisteredEntries]);
    } catch (e) {
      setClientsError(e instanceof Error ? e.message : `Failed to load ${PRODUCT_NAMES.AXON} gateways`);
    } finally {
      setClientsLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (selectedSn && gateways.length > 0 && !gateways.some((g) => g.sn === selectedSn)) {
      setSelectedSn(null);
      setQueryParams({ tenantId: selectedTenantId, gateway: null, tab: String(activeTab) });
    }
  }, [gateways, selectedSn, selectedTenantId, activeTab]);

  useEffect(() => {
    if (!selectedSn && gateways.length > 0) {
      const preferred = gateways.find((g) => g.isRegistered) ?? gateways[0];
      setSelectedSn(preferred.sn);
    }
  }, [gateways, selectedSn]);

  const efemAssignmentQuery = useQuery({
    queryKey: ['gateway-adapter-assignment', 'efem', selectedSn ?? ''],
    queryFn: async (): Promise<EfemAdapterAssignment | null> => {
      const res = await providerGatewayAdaptersService.getGatewayAdapterAssignment('efem', selectedSn!);
      const assignment = res.data.assignment;
      return assignment && 'efemAdapterKey' in assignment ? assignment : null;
    },
    enabled: !!selectedSn,
  });

  const efemAssignment = efemAssignmentQuery.data ?? selectedDevice?.efemAdapterAssignment ?? null;

  useEffect(() => {
    if (!selectedSn) {
      setSelectedDevice(null);
      return;
    }
    let cancelled = false;
    setDeviceLoading(true);
    void opsDevicesService
      .getDevice(selectedSn)
      .then((r) => {
        if (!cancelled) setSelectedDevice(r.data.device);
      })
      .catch(() => {
        if (!cancelled) setSelectedDevice(null);
      })
      .finally(() => {
        if (!cancelled) setDeviceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSn]);

  const handleTenantChange = useCallback((tenantId: string) => {
    setSelectedTenantId(tenantId);
    setSelectedSn(null);
    setGateways([]);
    setRegisteredSns(new Set());
    setQueryParams({ tenantId, gateway: null, tab: String(activeTab) });
  }, [activeTab]);

  const handleSelectGateway = (sn: string) => {
    setSelectedSn(sn);
    setQueryParams({ tenantId: selectedTenantId, gateway: sn, tab: String(activeTab) });
  };

  const tenantComboboxSx = useMemo(
    () => ({ minWidth: 280, width: { xs: '100%', sm: 320 } }) as const,
    [],
  );

  const toolbarContextControls = useMemo(
    () =>
      tenantsError ? (
        <Alert severity="error" sx={{ py: 0 }}>{tenantsError}</Alert>
      ) : (
        <SearchableTenantCombobox
          options={tenantOptions}
          value={selectedTenantId}
          onChange={handleTenantChange}
          loading={tenantsLoading}
          sx={tenantComboboxSx}
        />
      ),
    [handleTenantChange, selectedTenantId, tenantOptions, tenantsError, tenantsLoading, tenantComboboxSx],
  );

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadClients()} disabled={clientsLoading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadClients, clientsLoading],
  );

  useAppToolbar({ contextControls: toolbarContextControls, pageActions: toolbarPageActions });

  return (
    <AppPage sx={{ flex: 1, minHeight: 0, gap: 0 }}>
      {clientsError && (
        <Alert severity="error" sx={{ flexShrink: 0, mt: 1.5 }} onClose={() => setClientsError(null)}>
          {clientsError}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', mt: clientsError ? 1 : 0 }}>
        <EfemGatewaySplitLayout
          mobileShowPanel={!!selectedSn}
          list={
            <EfemGatewayListPanel count={gateways.length} loading={clientsLoading}>
              <EfemGatewayList
                gateways={gateways}
                selectedSn={selectedSn}
                onSelect={handleSelectGateway}
                loading={false}
              />
            </EfemGatewayListPanel>
          }
          panel={
            !selectedTenantId ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, p: 2 }}>
                <Typography variant="body1" color="text.secondary">Select a tenant to begin.</Typography>
              </Box>
            ) : !selectedSn ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, p: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  Select a gateway from the list to configure.
                </Typography>
              </Box>
            ) : (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(_, v: number) => {
                    setActiveTab(v);
                    setQueryParams({ tenantId: selectedTenantId, gateway: selectedSn, tab: String(v) });
                  }}
                  sx={{
                    flexShrink: 0,
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 1,
                  }}
                >
                  <Tab label="Adapter & configuration" />
                  <Tab label="Peripherals" />
                </Tabs>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
                  {activeTab === 0 && selectedSn && (
                    <Stack spacing={2}>
                      <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <GatewayAdapterAssignmentPanel
                          kind="efem"
                          serialNumber={selectedSn}
                          assignment={efemAssignment}
                          deviceProvisioning={
                            selectedDevice
                              ? {
                                  iotProvisioningStatus: selectedDevice.iotProvisioningStatus,
                                  thingName: selectedDevice.thingName,
                                }
                              : undefined
                          }
                          onAssigned={() => {
                            void efemAssignmentQuery.refetch();
                            void opsDevicesService.getDevice(selectedSn).then((r) => setSelectedDevice(r.data.device));
                            void loadClients();
                          }}
                        />
                      </Paper>
                      <Paper variant="outlined" sx={{ p: 2.5 }}>
                        {deviceLoading ? (
                          <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : (
                          <EfemGatewayConfigPanel
                            serialNumber={selectedSn}
                            tenantId={selectedTenantId || null}
                            hasAssignment={!!efemAssignment?.efemAdapterKey}
                          />
                        )}
                      </Paper>
                    </Stack>
                  )}
                  {activeTab === 1 && (
                    <EfemGatewayDetail
                      gatewaySerialNumber={selectedSn}
                      apiMode="provider"
                      tenantId={selectedTenantId}
                      onRefresh={loadClients}
                      isUnregistered={!registeredSns.has(selectedSn ?? '')}
                      inventoryStatus={gateways.find((g) => g.sn === selectedSn)?.inventoryStatus ?? 'claimed'}
                      tenantAllocated={selectedDevice ? !!selectedDevice.tenantId : false}
                    />
                  )}
                </Box>
              </>
            )
          }
        />
      </Box>
    </AppPage>
  );
}
